import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type Language = 'en' | 'de' | 'hr';

const STORAGE_KEY = 'gardenHub.language';

const SUPPORTED_LANGUAGES: Language[] = ['en', 'de', 'hr'];

function detectInitialLanguage(): Language {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		if (stored && SUPPORTED_LANGUAGES.includes(stored as Language)) {
			return stored as Language;
		}
	} catch {
		// Ignore storage failures and continue with browser detection.
	}

	const browserLanguage = navigator.language.toLowerCase();
	if (browserLanguage.startsWith('de')) return 'de';
	if (browserLanguage.startsWith('hr')) return 'hr';
	return 'en';
}

type Dictionary = Record<string, string>;
type LocaleMap = Record<Language, Dictionary>;

const dictionary: LocaleMap = {
	en: {
		'language.english': 'English',
		'language.german': 'Deutsch',
		'language.croatian': 'Hrvatski',
		'language.selector': 'Language selector',
		'public.menu': 'Menu',
		'public.closeMenu': 'Close menu',
		'public.openMenu': 'Open menu',
		'public.nav.home': 'Home',
		'public.nav.howItWorks': 'How it works',
		'public.nav.pricing': 'Pricing',
		'public.nav.calculator': 'Calculator',
		'public.nav.gallery': 'Gallery',
		'public.nav.resources': 'Resources',
		'public.cta.login': 'Log in',
		'public.cta.enterApp': 'Enter app',
		'public.footer.tagline': 'Garden collaboration playground.',
		'public.footer.questionnaire': 'Questionnaire',
		'public.footer.process': 'Process',
		'public.footer.stories': 'Stories',
		'public.footer.getStarted': 'Get started',
		'home.hero.title': 'Transform any outdoor space into your dream garden.',
		'home.hero.subtitle':
			'From vision to reality—create stunning gardens with intuitive planning tools, connect with expert gardeners, and watch your outdoor space come to life.',
		'home.hero.primaryCta.guest': 'Start building',
		'home.hero.primaryCta.user': 'Back to your dashboard',
		'home.hero.secondaryCta.guest': 'See how it works',
		'home.hero.secondaryCta.user': 'View live projects',
		'home.stats.platform': 'Platform, start to finish',
		'home.stats.one': 'One',
		'home.stats.styles': 'Garden styles',
		'home.stats.expert': 'Expert',
		'home.stats.gardeners': 'Gardeners ready',
		'home.process.unifiedTitle': 'How HortEra connects everyone',
		'home.process.unifiedSubtitle':
			'Three steps—from first garden idea to finished project. Each step shows what garden seekers do and how gardeners & companies respond.',
		'home.process.seekerLabel': 'Garden seeker',
		'home.process.companyLabel': 'Gardener or company',
		'home.process.unifiedCtaHint':
			'No account needed to try the questionnaire—sign in when you are ready to save a plan or browse the marketplace.',
		'home.process.seekerEyebrow': 'For garden seekers',
		'home.process.title': 'How it works',
		'home.process.subtitle':
			'Three steps from “I have a patch of grass” to a clear plan you can share—or turn into a real project.',
		'home.process.step1.title': 'Create your garden plan',
		'home.process.step1.description':
			'Short questionnaire about your space and style. Add a photo anytime for more tailored suggestions.',
		'home.process.step2.title': 'Receive offers from gardeners',
		'home.process.step2.description':
			'Compare budgets and timelines in one place when gardeners and companies respond to your plan.',
		'home.process.step3.title': 'Turn a plan into a project',
		'home.process.step3.description':
			'Choose an offer you like and track progress—ideas, photos, and messages stay in one hub.',
		'home.process.tryQuestionnaire': 'Try the questionnaire',
		'home.process.tryQuestionnaireHint':
			"No account needed to explore the steps—sign in when you're ready to unlock your plan.",
		'home.pro.eyebrow': 'For gardeners & companies',
		'home.pro.title': 'How HortEra works for your business',
		'home.pro.subtitle':
			'From browsing client plans to delivering finished gardens—quotes, crews, and client updates in one place.',
		'home.pro.step1.title': 'Browse the marketplace',
		'home.pro.step1.description':
			'See seeker plans with photos, style, and scope details—ready for quoting without an extra site visit.',
		'home.pro.step2.title': 'Send offers & quotes',
		'home.pro.step2.description':
			'Build professional quotes, respond to plans, and invite seekers to start with your company.',
		'home.pro.step3.title': 'Run projects & teams',
		'home.pro.step3.description':
			'Track tasks, schedule crews, share progress photos, and keep clients updated in one hub.',
		'home.pro.cta': 'Get started as a company',
		'home.pro.ctaHint': 'Sign in to browse plans, manage your team, and send your first offer.',
		'home.transforms.eyebrow': 'Transformations',
		'home.transforms.title': 'See what’s possible',
		'home.transforms.subtitle':
			'Drag the slider to compare before and after. Use the arrows to explore each story.',
		'home.quiz.eyebrow': 'Discover your style',
		'home.quiz.title': 'What kind of garden are you?',
		'home.quiz.subtitle': '3 quick questions. Instant personalised result.',
		'home.quiz.designCta': 'Design my garden',
		'home.quiz.retake': 'Retake quiz',
		'home.quiz.yourStyle': 'Your style is',
		'home.quiz.questionOf': 'Question {current} of {total}',
		'home.cta.title': 'Build your dream garden',
		'home.cta.subtitle':
			'Bring your garden vision to life with smart planning tools, expert guidance, and seamless project management.',
		'home.cta.bottomGuest': 'Start building',
		'home.cta.bottomUser': 'Return to dashboard',
		'home.cta.settings': 'Adjust settings',
		'home.showcase.title': 'Garden transformations',
		'home.showcase.subtitle':
			'See how any space can become a beautiful garden. Our before-and-after stories help garden seekers imagine every possibility while expert crews know exactly what to build.',
		'home.showcase.galleryTitle': 'Inspiration galleries',
		'home.showcase.galleryText':
			'Image sets, planting palettes, and irrigation notes come together in a single shareable hub.',
		'home.showcase.before': 'Before',
		'home.showcase.after': 'After',
		'home.showcase.details': 'View project details',
		'home.workspace.title': 'Built for everyone who loves gardens',
		'home.workspace.subtitle':
			"Whether you're dreaming up your perfect garden or bringing it to life, HortEra connects all the right people in one collaborative workspace.",
		'calc.title': 'Savings Calculator',
		'calc.subtitle':
			'Calculate how much money and time your company can save by using HortEra. Eliminate site visits, reduce planning time, cut revisions, and streamline your workflow.',
		'calc.hint': 'Enter your current costs • Select your plan • See your savings',
		'calc.quickStart': 'Quick Start (Load Preset)',
		'calc.smallCompany': 'Small Company',
		'calc.mediumCompany': 'Medium Company',
		'calc.largeCompany': 'Large Company',
		'calc.consultationHours': 'Consultation Hours',
		'gallery.hero.title': 'Project Gallery',
		'gallery.hero.subtitle':
			'Explore our completed garden transformations. Each project tells a unique story of vision, challenges, and beautiful results.',
		'gallery.featured.title': 'Featured Projects',
		'gallery.featured.subtitle':
			'Browse through our collection of completed garden transformations. Click on any project to see the full story.',
		'gallery.label.before': 'Before',
		'gallery.label.after': 'After',
		'gallery.cta.title': 'Ready to start your garden transformation?',
		'gallery.cta.subtitle':
			'Join our community of garden seekers and expert gardeners. Create your plan, get quotes, and bring your vision to life.',
		'gallery.cta.primary.user': 'Get Started',
		'gallery.cta.primary.guest': 'Sign Up Now',
		'gallery.cta.secondary': 'View Pricing',
		'gallery.modal.close': 'Close',
		'gallery.modal.details': 'Project Details',
		'gallery.modal.location': 'Location',
		'gallery.modal.area': 'Area',
		'gallery.modal.duration': 'Duration',
		'gallery.modal.budget': 'Budget',
		'gallery.modal.keyFeatures': 'Key Features',
		'gallery.modal.plantSelection': 'Plant Selection',
		'gallery.modal.story': 'Project Story',
		'gallery.modal.challenges': 'Challenges',
		'gallery.modal.solutions': 'Solutions',
		'gallery.modal.testimonial': 'Client Testimonial',
		'gallery.modal.info': 'Project Info',
		'gallery.modal.client': 'Client',
		'gallery.modal.completed': 'Completed',
		'gallery.modal.style': 'Style',
		'resources.hero.title': 'Resources',
		'resources.hero.subtitle':
			'Watch our introduction video and browse brochures. Share this page instead of emailing large files.',
		'resources.video.title': 'HortEra',
		'resources.video.subtitle': 'A short introduction to the HortEra platform.',
		'resources.video.unsupported':
			'Your browser does not support embedded video. Download the file instead.',
		'resources.video.download': 'Download video',
		'resources.brochures.title': 'Brochures',
		'resources.brochures.subtitle':
			'Open in your browser or download the PDF to share with colleagues and clients.',
		'resources.brochure.seeker.title': 'Seeker',
		'resources.brochure.seeker.desc':
			'Overview for garden owners — plan your space, get offers, and bring your vision to life.',
		'resources.brochure.company.title': 'Company',
		'resources.brochure.company.desc':
			'Overview for landscaping companies — streamline planning, quotes, and client collaboration.',
		'resources.brochure.view': 'View PDF',
		'resources.brochure.download': 'Download',
		'resources.cta.title': 'Want to see HortEra in action?',
		'resources.cta.subtitle':
			'Explore completed garden projects or try the questionnaire to start your own plan.',
		'resources.cta.gallery': 'View gallery',
		'resources.cta.questionnaire': 'Try questionnaire'
	},
	de: {
		'language.english': 'English',
		'language.german': 'Deutsch',
		'language.croatian': 'Hrvatski',
		'language.selector': 'Sprachauswahl',
		'public.menu': 'Menü',
		'public.closeMenu': 'Menü schließen',
		'public.openMenu': 'Menü öffnen',
		'public.nav.home': 'Startseite',
		'public.nav.howItWorks': 'So funktioniert es',
		'public.nav.pricing': 'Preise',
		'public.nav.calculator': 'Rechner',
		'public.nav.gallery': 'Galerie',
		'public.nav.resources': 'Ressourcen',
		'public.cta.login': 'Anmelden',
		'public.cta.enterApp': 'Zur App',
		'public.footer.tagline': 'Plattform für Garten-Zusammenarbeit.',
		'public.footer.questionnaire': 'Fragebogen',
		'public.footer.process': 'Ablauf',
		'public.footer.stories': 'Geschichten',
		'public.footer.getStarted': 'Loslegen',
		'home.hero.title': 'Verwandle jeden Außenbereich in deinen Traumgarten.',
		'home.hero.subtitle':
			'Von der Idee zur Realität: Erstelle beeindruckende Gärten mit intuitiven Planungstools, arbeite mit erfahrenen Gärtnern zusammen.',
		'home.hero.primaryCta.guest': 'Jetzt starten',
		'home.hero.primaryCta.user': 'Zurück zum Dashboard',
		'home.hero.secondaryCta.guest': 'So funktioniert es',
		'home.hero.secondaryCta.user': 'Projekte ansehen',
		'home.stats.platform': 'Plattform von Anfang bis Ende',
		'home.stats.one': 'Eine',
		'home.stats.styles': 'Gartenstile',
		'home.stats.expert': 'Experten',
		'home.stats.gardeners': 'Gärtner bereit',
		'home.process.unifiedTitle': 'So verbindet HortEra alle',
		'home.process.unifiedSubtitle':
			'Drei Schritte—von der ersten Gartenidee bis zum fertigen Projekt. Jeder Schritt zeigt, was Gartensuchende tun und wie Gärtner & Unternehmen reagieren.',
		'home.process.seekerLabel': 'Gartensuchende',
		'home.process.companyLabel': 'Gärtner oder Unternehmen',
		'home.process.unifiedCtaHint':
			'Kein Konto nötig für den Fragebogen—melden Sie sich an, wenn Sie einen Plan speichern oder den Marktplatz durchsuchen möchten.',
		'home.process.seekerEyebrow': 'Für Gartensuchende',
		'home.process.title': 'So funktioniert es',
		'home.process.subtitle':
			'Drei Schritte vom „Stück Rasen“ bis zu einem klaren Plan, den Sie teilen oder in ein echtes Projekt verwandeln können.',
		'home.process.step1.title': 'Gartenplan erstellen',
		'home.process.step1.description':
			'Kurzer Fragebogen zu Ihrem Raum und Stil. Fügen Sie jederzeit ein Foto für passendere Vorschläge hinzu.',
		'home.process.step2.title': 'Angebote von Gärtnern erhalten',
		'home.process.step2.description':
			'Vergleichen Sie Budgets und Zeitpläne an einem Ort, wenn Gärtner und Unternehmen auf Ihren Plan antworten.',
		'home.process.step3.title': 'Plan in ein Projekt verwandeln',
		'home.process.step3.description':
			'Wählen Sie ein Angebot und verfolgen Sie den Fortschritt—Ideen, Fotos und Nachrichten bleiben in einem Hub.',
		'home.process.tryQuestionnaire': 'Fragebogen ausprobieren',
		'home.process.tryQuestionnaireHint':
			'Kein Konto nötig, um die Schritte zu testen. Melde dich an, wenn du deinen Plan speichern möchtest.',
		'home.pro.eyebrow': 'Für Gärtner & Unternehmen',
		'home.pro.title': 'So funktioniert HortEra für Ihr Unternehmen',
		'home.pro.subtitle':
			'Vom Durchsuchen von Kundenplänen bis zur fertigen Gartenanlage—Angebote, Teams und Kundenupdates an einem Ort.',
		'home.pro.step1.title': 'Marktplatz durchsuchen',
		'home.pro.step1.description':
			'Sehen Sie Pläne mit Fotos, Stil und Umfang—bereit für Angebote ohne zusätzlichen Vor-Ort-Termin.',
		'home.pro.step2.title': 'Angebote & Kostenvoranschläge senden',
		'home.pro.step2.description':
			'Erstellen Sie professionelle Angebote, reagieren Sie auf Pläne und laden Sie Suchende zu Ihrem Unternehmen ein.',
		'home.pro.step3.title': 'Projekte & Teams steuern',
		'home.pro.step3.description':
			'Verfolgen Sie Aufgaben, planen Sie Teams, teilen Sie Fortschrittsfotos und halten Sie Kunden in einem Hub auf dem Laufenden.',
		'home.pro.cta': 'Als Unternehmen starten',
		'home.pro.ctaHint': 'Melden Sie sich an, um Pläne zu durchsuchen, Ihr Team zu verwalten und Ihr erstes Angebot zu senden.',
		'home.transforms.eyebrow': 'Transformationen',
		'home.transforms.title': 'Sehen Sie, was möglich ist',
		'home.transforms.subtitle':
			'Ziehen Sie den Schieberegler für Vorher/Nachher. Mit den Pfeilen weitere Geschichten entdecken.',
		'home.quiz.eyebrow': 'Entdecken Sie Ihren Stil',
		'home.quiz.title': 'Welcher Gartentyp sind Sie?',
		'home.quiz.subtitle': '3 kurze Fragen. Sofortiges, persönliches Ergebnis.',
		'home.quiz.designCta': 'Meinen Garten gestalten',
		'home.quiz.retake': 'Quiz wiederholen',
		'home.quiz.yourStyle': 'Ihr Stil ist',
		'home.quiz.questionOf': 'Frage {current} von {total}',
		'home.cta.title': 'Baue deinen Traumgarten',
		'home.cta.subtitle':
			'Setze deine Gartenidee mit smarter Planung, Expertenwissen und reibungslosem Projektablauf um.',
		'home.cta.bottomGuest': 'Jetzt starten',
		'home.cta.bottomUser': 'Zurück zum Dashboard',
		'home.cta.settings': 'Einstellungen',
		'home.showcase.title': 'Garten-Transformationen',
		'home.showcase.subtitle':
			'Sehen Sie, wie jeder Bereich zu einem schönen Garten werden kann. Vorher-Nachher-Geschichten helfen bei der Vorstellung und Umsetzung.',
		'home.showcase.galleryTitle': 'Inspirationsgalerien',
		'home.showcase.galleryText':
			'Bildersets, Pflanzpaletten und Bewässerungsnotizen kommen in einem zentralen Hub zusammen.',
		'home.showcase.before': 'Vorher',
		'home.showcase.after': 'Nachher',
		'home.showcase.details': 'Projektdetails ansehen',
		'home.workspace.title': 'Für alle gemacht, die Gärten lieben',
		'home.workspace.subtitle':
			'Egal ob Sie Ihren Traumgarten planen oder umsetzen: HortEra verbindet alle Beteiligten in einem gemeinsamen Workspace.',
		'calc.title': 'Einsparungsrechner',
		'calc.subtitle':
			'Berechnen Sie, wie viel Geld und Zeit Ihr Unternehmen mit HortEra sparen kann. Keine Vor-Ort-Besuche mehr, weniger Planungszeit und ein effizienterer Workflow.',
		'calc.hint': 'Aktuelle Kosten eingeben • Plan wählen • Einsparungen sehen',
		'calc.quickStart': 'Schnellstart (Voreinstellung laden)',
		'calc.smallCompany': 'Kleines Unternehmen',
		'calc.mediumCompany': 'Mittleres Unternehmen',
		'calc.largeCompany': 'Großes Unternehmen',
		'calc.consultationHours': 'Beratungsstunden',
		'gallery.hero.title': 'Projektgalerie',
		'gallery.hero.subtitle':
			'Entdecken Sie unsere abgeschlossenen Garten-Transformationen. Jedes Projekt erzählt eine eigene Geschichte von Vision, Herausforderungen und schönen Ergebnissen.',
		'gallery.featured.title': 'Ausgewählte Projekte',
		'gallery.featured.subtitle':
			'Durchsuchen Sie unsere Sammlung abgeschlossener Gartenprojekte. Klicken Sie auf ein Projekt, um die ganze Geschichte zu sehen.',
		'gallery.label.before': 'Vorher',
		'gallery.label.after': 'Nachher',
		'gallery.cta.title': 'Bereit, Ihre Garten-Transformation zu starten?',
		'gallery.cta.subtitle':
			'Werden Sie Teil unserer Community aus Garteninteressierten und Experten. Erstellen Sie Ihren Plan, holen Sie Angebote ein und verwirklichen Sie Ihre Vision.',
		'gallery.cta.primary.user': 'Jetzt starten',
		'gallery.cta.primary.guest': 'Jetzt registrieren',
		'gallery.cta.secondary': 'Preise ansehen',
		'gallery.modal.close': 'Schließen',
		'gallery.modal.details': 'Projektdetails',
		'gallery.modal.location': 'Ort',
		'gallery.modal.area': 'Fläche',
		'gallery.modal.duration': 'Dauer',
		'gallery.modal.budget': 'Budget',
		'gallery.modal.keyFeatures': 'Hauptmerkmale',
		'gallery.modal.plantSelection': 'Pflanzenauswahl',
		'gallery.modal.story': 'Projektverlauf',
		'gallery.modal.challenges': 'Herausforderungen',
		'gallery.modal.solutions': 'Lösungen',
		'gallery.modal.testimonial': 'Kundenstimme',
		'gallery.modal.info': 'Projektinfos',
		'gallery.modal.client': 'Kunde',
		'gallery.modal.completed': 'Abgeschlossen',
		'gallery.modal.style': 'Stil',
		'resources.hero.title': 'Ressourcen',
		'resources.hero.subtitle':
			'Sehen Sie unser Einführungsvideo und laden Sie Broschüren herunter. Teilen Sie diese Seite statt großer E-Mail-Anhänge.',
		'resources.video.title': 'HortEra',
		'resources.video.subtitle': 'Eine kurze Einführung in die HortEra-Plattform.',
		'resources.video.unsupported':
			'Ihr Browser unterstützt eingebettetes Video nicht. Laden Sie die Datei stattdessen herunter.',
		'resources.video.download': 'Video herunterladen',
		'resources.brochures.title': 'Broschüren',
		'resources.brochures.subtitle':
			'Im Browser öffnen oder als PDF herunterladen, um mit Kollegen und Kunden zu teilen.',
		'resources.brochure.seeker.title': 'Seeker',
		'resources.brochure.seeker.desc':
			'Überblick für Gartenbesitzer — planen Sie Ihren Raum, erhalten Sie Angebote und verwirklichen Sie Ihre Vision.',
		'resources.brochure.company.title': 'Company',
		'resources.brochure.company.desc':
			'Überblick für Gartenbauunternehmen — Planung, Angebote und Kundenzusammenarbeit effizienter gestalten.',
		'resources.brochure.view': 'PDF ansehen',
		'resources.brochure.download': 'Herunterladen',
		'resources.cta.title': 'HortEra in Aktion sehen?',
		'resources.cta.subtitle':
			'Entdecken Sie abgeschlossene Gartenprojekte oder testen Sie den Fragebogen für Ihren eigenen Plan.',
		'resources.cta.gallery': 'Galerie ansehen',
		'resources.cta.questionnaire': 'Fragebogen testen'
	},
	hr: {
		'language.english': 'English',
		'language.german': 'Deutsch',
		'language.croatian': 'Hrvatski',
		'language.selector': 'Odabir jezika',
		'public.menu': 'Izbornik',
		'public.closeMenu': 'Zatvori izbornik',
		'public.openMenu': 'Otvori izbornik',
		'public.nav.home': 'Početna',
		'public.nav.howItWorks': 'Kako funkcionira',
		'public.nav.pricing': 'Cijene',
		'public.nav.calculator': 'Kalkulator',
		'public.nav.gallery': 'Galerija',
		'public.nav.resources': 'Resursi',
		'public.cta.login': 'Prijava',
		'public.cta.enterApp': 'Uđi u aplikaciju',
		'public.footer.tagline': 'Platforma za suradnju u vrtlarstvu.',
		'public.footer.questionnaire': 'Upitnik',
		'public.footer.process': 'Proces',
		'public.footer.stories': 'Priče',
		'public.footer.getStarted': 'Počni',
		'home.hero.title': 'Pretvorite svaki vanjski prostor u vrt iz snova.',
		'home.hero.subtitle':
			'Od ideje do realizacije - stvarajte prekrasne vrtove uz jednostavne alate za planiranje i povežite se sa stručnim vrtlarima.',
		'home.hero.primaryCta.guest': 'Počni graditi',
		'home.hero.primaryCta.user': 'Natrag na nadzornu ploču',
		'home.hero.secondaryCta.guest': 'Pogledaj kako radi',
		'home.hero.secondaryCta.user': 'Pogledaj aktivne projekte',
		'home.stats.platform': 'Platforma od početka do kraja',
		'home.stats.one': 'Jedna',
		'home.stats.styles': 'Vrtnih stilova',
		'home.stats.expert': 'Stručni',
		'home.stats.gardeners': 'Vrtlari spremni',
		'home.process.unifiedTitle': 'Kako HortEra povezuje sve',
		'home.process.unifiedSubtitle':
			'Tri koraka—od prve ideje o vrtu do gotovog projekta. Svaki korak pokazuje što rade vlasnici vrta i kako vrtlari i tvrtke odgovaraju.',
		'home.process.seekerLabel': 'Vlasnik vrta',
		'home.process.companyLabel': 'Vrtlar ili tvrtka',
		'home.process.unifiedCtaHint':
			'Račun nije potreban za upitnik—prijavite se kada ste spremni spremiti plan ili pregledati tržište.',
		'home.process.seekerEyebrow': 'Za vlasnike vrta',
		'home.process.title': 'Kako funkcionira',
		'home.process.subtitle':
			'Tri koraka od „imam komad travnjaka“ do jasnog plana koji možete podijeliti ili pretvoriti u pravi projekt.',
		'home.process.step1.title': 'Izradite plan vrta',
		'home.process.step1.description':
			'Kratki upitnik o vašem prostoru i stilu. Dodajte fotografiju bilo kada za prilagođenije prijedloge.',
		'home.process.step2.title': 'Primite ponude od vrtlara',
		'home.process.step2.description':
			'Usporedite proračune i rokove na jednom mjestu kada vrtlari i tvrtke odgovore na vaš plan.',
		'home.process.step3.title': 'Pretvorite plan u projekt',
		'home.process.step3.description':
			'Odaberite ponudu koja vam odgovara i pratite napredak—ideje, fotografije i poruke ostaju na jednom mjestu.',
		'home.process.tryQuestionnaire': 'Isprobaj upitnik',
		'home.process.tryQuestionnaireHint':
			'Nije potreban račun za isprobavanje. Prijavite se kada želite spremiti svoj plan.',
		'home.pro.eyebrow': 'Za vrtlare i tvrtke',
		'home.pro.title': 'Kako HortEra radi za vaše poslovanje',
		'home.pro.subtitle':
			'Od pregleda planova klijenata do gotovih vrtova—ponude, timovi i ažuriranja klijenata na jednom mjestu.',
		'home.pro.step1.title': 'Pregledajte tržište',
		'home.pro.step1.description':
			'Pogledajte planove s fotografijama, stilom i opsegom—spremne za ponudu bez dodatnog obilaska.',
		'home.pro.step2.title': 'Pošaljite ponude i procjene',
		'home.pro.step2.description':
			'Izradite profesionalne ponude, odgovorite na planove i pozovite vlasnike vrta da započnu s vašom tvrtkom.',
		'home.pro.step3.title': 'Vodite projekte i timove',
		'home.pro.step3.description':
			'Pratite zadatke, rasporedite timove, dijelite fotografije napretka i držite klijente u tijeku na jednom mjestu.',
		'home.pro.cta': 'Započnite kao tvrtka',
		'home.pro.ctaHint': 'Prijavite se za pregled planova, upravljanje timom i slanje prve ponude.',
		'home.transforms.eyebrow': 'Transformacije',
		'home.transforms.title': 'Pogledajte što je moguće',
		'home.transforms.subtitle':
			'Povucite klizač za usporedbu prije i poslije. Strelice za pregled svake priče.',
		'home.quiz.eyebrow': 'Otkrijte svoj stil',
		'home.quiz.title': 'Kakav ste vrt?',
		'home.quiz.subtitle': '3 brza pitanja. Trenutni, personalizirani rezultat.',
		'home.quiz.designCta': 'Dizajniraj moj vrt',
		'home.quiz.retake': 'Ponovi kviz',
		'home.quiz.yourStyle': 'Vaš stil je',
		'home.quiz.questionOf': 'Pitanje {current} od {total}',
		'home.cta.title': 'Izgradite vrt iz snova',
		'home.cta.subtitle':
			'Oživite svoju viziju vrta uz pametno planiranje, stručnu podršku i jednostavno upravljanje projektom.',
		'home.cta.bottomGuest': 'Počni graditi',
		'home.cta.bottomUser': 'Povratak na nadzornu ploču',
		'home.cta.settings': 'Postavke',
		'home.showcase.title': 'Transformacije vrtova',
		'home.showcase.subtitle':
			'Pogledajte kako svaki prostor može postati prekrasan vrt. Priče prije i poslije pomažu lakše zamisliti rezultat.',
		'home.showcase.galleryTitle': 'Galerije inspiracije',
		'home.showcase.galleryText':
			'Skupovi slika, biljne palete i bilješke o navodnjavanju na jednom mjestu koje se lako dijeli.',
		'home.showcase.before': 'Prije',
		'home.showcase.after': 'Poslije',
		'home.showcase.details': 'Pogledaj detalje projekta',
		'home.workspace.title': 'Za sve koji vole vrtove',
		'home.workspace.subtitle':
			'Bilo da sanjate savršen vrt ili ga već gradite, HortEra povezuje prave ljude u jednom zajedničkom prostoru.',
		'calc.title': 'Kalkulator uštede',
		'calc.subtitle':
			'Izračunajte koliko novca i vremena vaša tvrtka može uštedjeti s HortErom. Uklonite terenske obilaske i ubrzajte planiranje.',
		'calc.hint': 'Unesite troškove • Odaberite paket • Pogledajte uštedu',
		'calc.quickStart': 'Brzi početak (učitaj predložak)',
		'calc.smallCompany': 'Mala tvrtka',
		'calc.mediumCompany': 'Srednja tvrtka',
		'calc.largeCompany': 'Velika tvrtka',
		'calc.consultationHours': 'Sati konzultacija',
		'gallery.hero.title': 'Galerija projekata',
		'gallery.hero.subtitle':
			'Istražite naše završene transformacije vrtova. Svaki projekt priča jedinstvenu priču o viziji, izazovima i prekrasnim rezultatima.',
		'gallery.featured.title': 'Istaknuti projekti',
		'gallery.featured.subtitle':
			'Pregledajte našu kolekciju završenih vrtnih transformacija. Kliknite na projekt kako biste vidjeli cijelu priču.',
		'gallery.label.before': 'Prije',
		'gallery.label.after': 'Poslije',
		'gallery.cta.title': 'Spremni započeti transformaciju svog vrta?',
		'gallery.cta.subtitle':
			'Pridružite se našoj zajednici ljubitelja vrtova i stručnih vrtlara. Izradite plan, zatražite ponude i oživite svoju viziju.',
		'gallery.cta.primary.user': 'Započni',
		'gallery.cta.primary.guest': 'Registriraj se',
		'gallery.cta.secondary': 'Pogledaj cijene',
		'gallery.modal.close': 'Zatvori',
		'gallery.modal.details': 'Detalji projekta',
		'gallery.modal.location': 'Lokacija',
		'gallery.modal.area': 'Površina',
		'gallery.modal.duration': 'Trajanje',
		'gallery.modal.budget': 'Budžet',
		'gallery.modal.keyFeatures': 'Ključne značajke',
		'gallery.modal.plantSelection': 'Odabir biljaka',
		'gallery.modal.story': 'Priča projekta',
		'gallery.modal.challenges': 'Izazovi',
		'gallery.modal.solutions': 'Rješenja',
		'gallery.modal.testimonial': 'Izjava klijenta',
		'gallery.modal.info': 'Informacije o projektu',
		'gallery.modal.client': 'Klijent',
		'gallery.modal.completed': 'Završeno',
		'gallery.modal.style': 'Stil',
		'resources.hero.title': 'Resursi',
		'resources.hero.subtitle':
			'Pogledajte uvodni video i preuzmite brošure. Podijelite ovu stranicu umjesto slanja velikih datoteka e-poštom.',
		'resources.video.title': 'HortEra',
		'resources.video.subtitle': 'Kratak uvod u HortEra platformu.',
		'resources.video.unsupported':
			'Vaš preglednik ne podržava ugrađeni video. Umjesto toga preuzmite datoteku.',
		'resources.video.download': 'Preuzmi video',
		'resources.brochures.title': 'Brošure',
		'resources.brochures.subtitle':
			'Otvorite u pregledniku ili preuzmite PDF za dijeljenje s kolegama i klijentima.',
		'resources.brochure.seeker.title': 'Seeker',
		'resources.brochure.seeker.desc':
			'Pregled za vlasnike vrtova — isplanirajte prostor, zatražite ponude i ostvarite svoju viziju.',
		'resources.brochure.company.title': 'Company',
		'resources.brochure.company.desc':
			'Pregled za vrtne tvrtke — pojednostavite planiranje, ponude i suradnju s klijentima.',
		'resources.brochure.view': 'Pogledaj PDF',
		'resources.brochure.download': 'Preuzmi',
		'resources.cta.title': 'Želite vidjeti HortEru u akciji?',
		'resources.cta.subtitle':
			'Istražite završene vrtne projekte ili isprobajte upitnik za vlastiti plan.',
		'resources.cta.gallery': 'Pogledaj galeriju',
		'resources.cta.questionnaire': 'Isprobaj upitnik'
	}
};

type I18nContextValue = {
	language: Language;
	setLanguage: (language: Language) => void;
	t: (key: string, fallback?: string) => string;
};

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
	const [language, setLanguage] = useState<Language>(detectInitialLanguage);

	useEffect(() => {
		try {
			localStorage.setItem(STORAGE_KEY, language);
		} catch {
			// Ignore storage failures.
		}
		document.documentElement.lang = language;
	}, [language]);

	const value = useMemo<I18nContextValue>(() => {
		return {
			language,
			setLanguage,
			t: (key: string, fallback?: string) =>
				dictionary[language][key] ?? dictionary.en[key] ?? fallback ?? key
		};
	}, [language]);

	return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
	const ctx = useContext(I18nContext);
	if (!ctx) {
		throw new Error('useI18n must be used inside I18nProvider');
	}
	return ctx;
}
